import { Select as HeroUISelect } from "@heroui/react";
import { WithId } from "core/types";
import { ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

type LabelPlacement = HeroUISelectProps["labelPlacement"];
type Variant = "bordered" | "flat";
type VariantProp =
  | "outline"
  | "inline"
  | "minimal"
  | "horizontal"
  | "horizontalOutline";
type HeroUISelectProps = React.ComponentProps<typeof HeroUISelect>;
type SelectOverrideVariants = VariantProps<typeof selectOverrides>;
type Size = "md" | "lg" | "sm";

export const selectOverrides = tv({
  compoundVariants: [
    {
      class: {
        trigger: "data-[hover=true]:ring-danger data-[hover=true]:ring-2",
      },
      isInvalid: true,
      variant: "bordered",
    },
    {
      class: {
        trigger: "data-[hover=true]:ring-black data-[hover=true]:ring-2",
      },
      isInvalid: false,
      variant: "bordered",
    },
    // MINIMAL
    {
      class: {
        innerWrapper: "pt-0!",
        label: "group-data-[has-value=true]:hidden!",
        value: "text-sm!",
      },
      labelPlacement: "inside",
      variant: "flat",
    },
  ],
  defaultVariants: {
    color: "primary",
    disableSelectorIconRotation: false,
    fullWidth: true,
    isDisabled: false,
    isMultiline: false,
    labelPlacement: "inside",
    size: "md",
    variant: "bordered",
  },
  slots: {
    base: "",
    errorMessage: "",
    innerWrapper: "",
    label: "after:text-gray-900! font-semibold",
    selectorIcon: "text-gray-900",
    trigger: "bg-transparent!",
    value: "group-data-[has-value=true]:text-gray-900",
  },
  variants: {
    color: {
      primary: {
        trigger:
          "data-[focus=true]:ring-primary data-[open=true]:ring-primary data-[focus=true]:ring-2 data-[open=true]:ring-2 outline-hidden!",
      },
    },
    isDisabled: {
      true: {
        label: "text-default-disabled!",
        selectorIcon: "text-default-disabled!",
        trigger:
          "border-default-disabled! data-[focus=true]:ring-transparent data-[open=true]:ring-transparent text-default-disabled!",
        value: "text-default-disabled!",
      },
    },
    isInvalid: {
      true: {
        errorMessage: "text-danger-dark!",
        label: "text-gray-900!",
        selectorIcon: "text-danger!",
        trigger:
          "border-danger! data-[focus=true]:border-transparent! data-[open=true]:border-transparent!",
        value: "text-danger!",
      },
    },
    labelPlacement: {
      inside: {
        label: "text-gray-600 text-sm!",
        value: "text-sm!",
      },
      outside: {},
      "outside-left": {
        label: "text-foreground text-sm!",
        trigger: "min-w-[80px]",
      },
      "outside-top": {
        label: "text-gray-900! pb-1.5",
      },
    },
    size: {
      lg: {
        label: "text-medium",
        trigger: "h-12! min-h-12! rounded-large!",
        value: "text-medium",
      },
      md: {
        label: "text-small",
        trigger: "h-10! min-h-10! rounded-medium!",
        value: "text-small",
      },
      sm: {
        label: "text-sm",
        trigger: "h-8! min-h-8! px-2! rounded-small!",
        value: "text-small",
      },
    },
    variant: {
      bordered: {
        trigger: "border-1! border-gray-600 data-[hover=true]:text-gray-900",
      },
      flat: {
        trigger: "border-transparent!",
      },
    },
  },
});

export type SelectProps<T extends WithId = WithId> = Omit<
  HeroUISelectProps,
  | "variant"
  | "color"
  | "radius"
  | "labelPlacement"
  | "size"
  | "children"
  | "classNames"
> &
  Pick<SelectOverrideVariants, "color" | "size"> & {
    "data-testid"?: string;
    items: T[];
    renderItem: (item: T) => ReactNode;
    variant?: VariantProp;
  };

export function mapVariant(variant?: VariantProp): {
  variant: Variant;
  labelPlacement: LabelPlacement;
} {
  if (variant === "outline")
    return {
      labelPlacement: "outside-top",
      variant: "bordered",
    };
  if (variant === "minimal")
    return {
      labelPlacement: "inside",
      variant: "flat",
    };
  if (variant === "inline")
    return {
      labelPlacement: "inside",
      variant: "bordered",
    };
  if (variant === "horizontal")
    return {
      labelPlacement: "outside-left",
      variant: "flat",
    };
  if (variant === "horizontalOutline")
    return {
      labelPlacement: "outside-left",
      variant: "bordered",
    };
  return {
    labelPlacement: "inside",
    variant: "bordered",
  };
}

export function getMergedClassNames({
  isDisabled,
  isInvalid,
  size = "md",
  variantProp,
}: {
  variantProp?: VariantProp;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: Size;
}) {
  const { labelPlacement, variant } = mapVariant(variantProp);
  const overrides = selectOverrides({
    color: "primary",
    isDisabled: isDisabled ?? false,
    isInvalid: isInvalid ?? false,
    labelPlacement,
    size,
    variant,
  });

  return {
    base: [overrides.base()].filter(Boolean).join(" "),
    errorMessage: [overrides.errorMessage()].filter(Boolean).join(" "),
    innerWrapper: [overrides.innerWrapper()].filter(Boolean).join(" "),
    label: [overrides.label()].filter(Boolean).join(" "),
    selectorIcon: [overrides.selectorIcon()].filter(Boolean).join(" "),
    trigger: [overrides.trigger()].filter(Boolean).join(" "),
    value: [overrides.value()].filter(Boolean).join(" "),
  };
}
